import { Box, Paper, Typography } from '@mui/material';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { ContentPanel } from '../../../../../../components/ContentPanel';
import { EmptyPlaceholder } from '@components/EmptyPlaceholder';
import { useGetPortalScoreSubmissions } from '@reactQuery/competitions';
import { Loader } from '@components/feed/Loader';
import { useTranslation } from 'react-i18next';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import { PortalScoreSubmission } from '@api/competitionsApi/interfaces';
import { IYourScoresTab } from './interfaces';
import { ViewScorecardDialog } from './components/ViewScorecardDialog';
import { DeletePendingScoreSubmissionDialog } from './components/DeletePendingScoreSubmissionDialog';
import { getColumns } from './helpers';

export const YourScoresTab: React.FC<IYourScoresTab> = ({
  slug,
}): JSX.Element => {
  const { t } = useTranslation();
  const { data = [], isLoading } = useGetPortalScoreSubmissions(slug);
  const [selectedSubmission, setSelectedSubmission] =
    useState<PortalScoreSubmission | null>(null);
  const [submissionToDelete, setSubmissionToDelete] =
    useState<PortalScoreSubmission | null>(null);

  const columns = useMemo(
    () =>
      getColumns({
        onViewScorecard: setSelectedSubmission,
      }),
    []
  );

  const approvedScores = useMemo(
    () => data.filter((submission) => submission.status === 'approved'),
    [data]
  );

  useEffect(() => {
    if (!selectedSubmission) {
      return;
    }

    const stillExists = data.some(
      (submission) => submission.id === selectedSubmission.id
    );

    if (!stillExists) {
      setSelectedSubmission(null);
      setSubmissionToDelete(null);
    }
  }, [data, selectedSubmission]);

  const stats = useMemo(() => {
    if (approvedScores.length === 0) {
      return null;
    }

    const values = approvedScores.map((submission) => submission.value);
    const average =
      values.reduce((total, value) => total + value, 0) / values.length;
    const highest = Math.max(...values);

    return {
      average: average.toFixed(1),
      highest: highest.toFixed(1),
    };
  }, [approvedScores]);

  if (isLoading) {
    return (
      <Box py={4}>
        <Loader />
      </Box>
    );
  }

  return (
    <Fragment>
      {stats && (
        <ContentPanel borderRadius="none">
          <Typography variant="body1">
            <strong>{t('competitions:portal.yourScores.average')}:</strong>{' '}
            {stats.average} |{' '}
            <strong>{t('competitions:portal.yourScores.highest')}:</strong>{' '}
            {stats.highest}
          </Typography>
        </ContentPanel>
      )}

      {data.length === 0 ? (
        <ContentPanel borderRadius="none" sx={{ mt: '10px' }}>
          <EmptyPlaceholder
            icon={ScoreboardIcon}
            title={t(
              'competitions:portal.yourScores.emptyPlaceholders.noScores.title'
            )}
            subTitle={t(
              'competitions:portal.yourScores.emptyPlaceholders.noScores.subTitle'
            )}
          />
        </ContentPanel>
      ) : (
        <Box
          sx={{
            mt: '10px',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflowX: 'auto',
          }}
        >
          <Paper
            sx={{
              minWidth: '100%',
              width: 'max-content',
            }}
          >
            <DataGrid
              rows={data}
              columns={columns}
              disableRowSelectionOnClick
              hideFooter
              autoHeight
              onRowClick={(params) => setSelectedSubmission(params.row)}
              sx={{
                border: 0,
                minWidth: '100%',
                '& .MuiDataGrid-columnHeader': {
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
              }}
            />
          </Paper>
        </Box>
      )}

      <ViewScorecardDialog
        show={selectedSubmission !== null}
        imageId={selectedSubmission?.scorecardImageId ?? null}
        value={selectedSubmission?.value}
        shotOn={selectedSubmission?.shotOn}
        weaponGroup={selectedSubmission?.weaponGroup}
        note={selectedSubmission?.note}
        reviewNote={selectedSubmission?.reviewNote}
        status={selectedSubmission?.status}
        onDelete={
          selectedSubmission?.status === 'pending'
            ? () => setSubmissionToDelete(selectedSubmission)
            : undefined
        }
        onClose={() => setSelectedSubmission(null)}
      />

      <DeletePendingScoreSubmissionDialog
        show={submissionToDelete !== null}
        slug={slug}
        submission={submissionToDelete}
        onClose={() => setSubmissionToDelete(null)}
        onDeleted={() => {
          setSubmissionToDelete(null);
          setSelectedSubmission(null);
        }}
      />
    </Fragment>
  );
};
